import { Box, Tooltip } from '@mui/joy';

import { Button } from 'core/components';
import { ButtonFileUpload, ButtonGroup, MenuButton } from 'core/elements';
import { ActionButtonType } from 'core/enums';
import { ITableAction } from 'core/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  action: ITableAction;
}

interface IButtonGroupPositionProps {
  'data-first-child'?: string;
  'data-last-child'?: string;
}

const ActionButtonView = ({ action, ...rest }: IProps) => {
  if (action.hide) {
    return null;
  }

  switch (action.type) {
    case ActionButtonType.MENU: {
      const menuButton = (
        <MenuButton
          label={action?.label}
          menuItems={action?.menuItems}
          variant={action?.variant || Variant.SOFT}
          color={action?.color || Color.PRIMARY}
          startDecorator={action?.startDecorator}
          endDecorator={action?.endDecorator}
          icon={action?.icon}
          disabled={action?.disabled}
          sx={action?.sx}
          {...rest}
        />
      );

      if (!action.tooltipProps) {
        return menuButton;
      }

      // A disabled button gets no hover, so the Tooltip sits on a wrapper Box. Inside a Joy
      // ButtonGroup that Box is the group's direct child and must carry its position markers
      // (same as core Button).
      const groupPosition = rest as IButtonGroupPositionProps;

      return (
        <Tooltip {...action.tooltipProps}>
          <Box
            sx={{ position: 'relative' }}
            data-first-child={groupPosition['data-first-child']}
            data-last-child={groupPosition['data-last-child']}
          >
            {menuButton}
          </Box>
        </Tooltip>
      );
    }
    case ActionButtonType.BUTTON:
      return (
        <Button
          text={action?.label}
          variant={action?.variant || Variant.SOFT}
          color={action?.color || Color.PRIMARY}
          startDecorator={action?.startDecorator}
          disabled={action?.disabled}
          onClick={action?.onClick}
          sx={action?.sx}
          tooltipProps={action?.tooltipProps}
          {...rest}
        />
      );
    case ActionButtonType.GROUP:
      return (
        <ButtonGroup
          color={action?.color}
          variant={action?.variant}
          orientation={action?.orientation}
          buttons={action?.buttons || []}
          sx={action?.sx}
          {...rest}
        />
      );
    case ActionButtonType.BUTTON_FILE_UPLOAD:
      return (
        <ButtonFileUpload
          text={action?.label}
          variant={action?.variant || Variant.SOFT}
          color={action?.color || Color.PRIMARY}
          startDecorator={action?.startDecorator}
          disabled={action?.disabled}
          onUpload={action?.onUpload}
          sx={action?.sx}
          {...rest}
        />
      );
    default:
      return null;
  }
};

export default ActionButtonView;
